import React, { useReducer, useContext, useEffect, useCallback, useRef } from 'react';
import { IGames, IGetPaginatedGames, IAssignGames } from 'types';
import { IAppContext, AppContext } from 'appState/appContext';
import { gameAssignmentReducer } from './gameAssignmentReducer';
import { GameAssignmentActionTypes } from './gameAssignmentActions';
import {
	gameAssignmentInitialState,
	GameAssignmentContext,
	TActiveItemToDisplay,
	TTypeOfModalWindow,
	IIDsSelectedItems,
	IGamesWithAdditionalProperty,
	TSelectedItem,
	TGamesSortBy,
	IGameWithAdditionalField,
} from './gameAssignmentContext';
import {
	getPaginatedGames,
	getCustomersForAssignGame,
	getRestaurantsByIDsCustomersForGames,
	getRestaurantsByIDsRestaurantsForGames,
	assignGames,
} from 'backend/games';
import { useFetchData } from 'hooks/useFetchData';
import { AxiosResponse } from 'axios';
import { createAppError } from 'utils/helpers';

const GameAssignmentState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(gameAssignmentReducer, gameAssignmentInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IGames>, IGetPaginatedGames>(
		isMounted.current ? getPaginatedGames : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString,
			categoryId: state.selectedCategory,
			developerId: state.selectedDeveloper,
			isUnassignedGames: state.isUnassignedGames,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
			sortByApplicationId: null,
			sortByVersionCode: null,
			sortByVersionName: null,
			sortByCategories: null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: GameAssignmentActionTypes.SET_GAMES_FOR_ASSIGN,
				payload: {
					gamesForAssign: response.data,
				},
			});
		}
	}, [response]);

	const changeSearchString = (value: string) => {
		dispatch({
			type: GameAssignmentActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const changeSearchStringForAllCustomers = useCallback((value: string) => {
		dispatch({
			type: GameAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_ALL_CUSTOMERS,
			payload: {
				searchStringForAllCustomers: value,
			},
		});
	}, []);

	const changeSearchStringForRestaurants = useCallback((value: string) => {
		dispatch({
			type: GameAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_RESTAURANTS,
			payload: {
				searchStringForRestaurants: value,
			},
		});
	}, []);

	const changeSearchStringForGames = useCallback((value: string) => {
		dispatch({
			type: GameAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_GAMES,
			payload: {
				searchStringForGames: value,
			},
		});
	}, []);

	const setCategory = (value: TSelectedItem) => {
		dispatch({
			type: GameAssignmentActionTypes.SET_CATEGORY,
			payload: {
				selectedCategory: value,
			},
		});
	};

	const setDeveloper = (value: TSelectedItem) => {
		dispatch({
			type: GameAssignmentActionTypes.SET_DEVELOPER,
			payload: {
				selectedDeveloper: value,
			},
		});
	};

	const toggleUnassignedGames = () => {
		dispatch({
			type: GameAssignmentActionTypes.TOGGLE_UNASSIGNED_GAMES,
		});
	};

	const resetSelectedControls = () => dispatch({ type: GameAssignmentActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TGamesSortBy) => {
		dispatch({
			type: GameAssignmentActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setIDsSelectedGame = (value: string[]) => {
		dispatch({
			type: GameAssignmentActionTypes.SET_IDS_SELECTED_GAME,
			payload: {
				IDsSelectedGame: value,
			},
		});
	};

	const changeActiveItemToDisplay = (value: TActiveItemToDisplay) => {
		dispatch({
			type: GameAssignmentActionTypes.CHANGE_ACTIVE_ITEM_TO_DISPLAY,
			payload: {
				activeItemToDisplay: value,
			},
		});
	};

	const setShowBreadcrumbs = (value?: boolean) => {
		const isShowBreadcrumbs = value !== undefined ? value : !state.isShowBreadcrumbs;
		dispatch({
			type: GameAssignmentActionTypes.SET_SHOW_BREADCRUMBS,
			payload: {
				isShowBreadcrumbs,
			},
		});
	};

	const setAllCustomers = async () => {
		try {
			showLoader();
			const response = await getCustomersForAssignGame();
			dispatch({
				type: GameAssignmentActionTypes.SET_ALL_CUSTOMERS,
				payload: {
					allCustomers: response.data.customers,
				},
			});
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const setIDsSelectedCustomers = async (value: IIDsSelectedItems, isUpdateRestaurantsList: boolean) => {
		try {
			if (Object.keys(value).length === 0 && isUpdateRestaurantsList) {
				dispatch({
					type: GameAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: {},
						restaurants: [],
						IDsSelectedRestaurants: {},
						games: [],
					},
				});
			} else if (isUpdateRestaurantsList) {
				const arrWithCustomerIDs = Object.keys(value);
				showLoader();
				const response = await getRestaurantsByIDsCustomersForGames(arrWithCustomerIDs);
				const allRestaurantIDs = response.data.customers
					.filter((customer) => customer.restaurants.length)
					.map((customer) => customer.restaurants.map((restaurant) => restaurant.restaurantId))
					.flat();
				const updatedIDsSelectedRestaurants = allRestaurantIDs.reduce<IIDsSelectedItems>((acc, key) => {
					if (key in state.IDsSelectedRestaurants) {
						acc[key] = state.IDsSelectedRestaurants[key];
					}
					return acc;
				}, {});
				const updatedGames = state.games.filter((el) => allRestaurantIDs.includes(el.restaurantId));
				dispatch({
					type: GameAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: value,
						restaurants: response.data.customers,
						IDsSelectedRestaurants: updatedIDsSelectedRestaurants,
						games: updatedGames,
					},
				});
			} else {
				// just to open and close tabs with restaurants.
				dispatch({
					type: GameAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: value,
						restaurants: state.restaurants,
						IDsSelectedRestaurants: state.IDsSelectedRestaurants,
						games: state.games,
					},
				});
			}
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const setIDsSelectedRestaurants = async (value: IIDsSelectedItems, isUpdateGamesList: boolean) => {
		try {
			if (Object.keys(value).length === 0 && isUpdateGamesList) {
				dispatch({
					type: GameAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: {},
						games: [],
					},
				});
			} else if (isUpdateGamesList) {
				const arrWithRestaurantIDs = Object.keys(value);
				showLoader();
				const response = await getRestaurantsByIDsRestaurantsForGames(arrWithRestaurantIDs);
				const IDsRestaurantFromState = state.games.map((el) => el.restaurantId);
				const IDsRestaurantFromResponse = response.data.restaurants.map((el) => el.restaurantId);
				const filteredGamesFromState = state.games.filter((el) =>
					IDsRestaurantFromResponse.includes(el.restaurantId),
				);
				const filteredGamesFromResponse = response.data.restaurants
					.filter((el) => !IDsRestaurantFromState.includes(el.restaurantId))
					.map((el) => ({
						restaurantId: el.restaurantId,
						restaurantAddress: el.restaurantAddress,
						isChanged: false,
						games: el.games.map((game) => ({ ...game, isAddedDishFromList: false })),
					}));
				dispatch({
					type: GameAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: value,
						games: [...filteredGamesFromState, ...filteredGamesFromResponse],
					},
				});
			} else {
				// just to open and close tabs with games.
				dispatch({
					type: GameAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: value,
						games: state.games,
					},
				});
			}
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const addSelectedGamesToRestaurant = () => {
		const selectedGamesFromGameList = state.IDsSelectedGame.reduce<IGameWithAdditionalField[]>((acc, id) => {
			const searchGame = state.gamesForAssign.games.find((el) => el.id === id);
			if (searchGame) {
				acc.push({
					gameId: searchGame.id,
					gameName: searchGame.name,
					customerName: null,
					restaurantAddress: null,
					isAddedDishFromList: true,
				});
			}
			return acc;
		}, []);

		const IDsOfOpenRestaurantTabs = Object.keys(state.IDsSelectedRestaurants).filter(
			(id) => state.IDsSelectedRestaurants[id] === true,
		);

		const updatedGames = state.games.map<IGamesWithAdditionalProperty>((restaurant) => {
			if (!IDsOfOpenRestaurantTabs.includes(restaurant.restaurantId)) {
				return restaurant;
			}
			const IDsOfAvailableGames = restaurant.games.map((game) => game.gameId);
			const gamesForAdding = selectedGamesFromGameList.filter(
				(game) => !IDsOfAvailableGames.includes(game.gameId),
			);
			return {
				...restaurant,
				isChanged: true,
				games: [...restaurant.games, ...gamesForAdding],
			};
		});

		dispatch({
			type: GameAssignmentActionTypes.SET_GAMES,
			payload: {
				games: updatedGames,
				IDsSelectedGame: [],
			},
		});
	};

	const deleteGameFromRestaurant = (restaurantID: string, gameID: string) => {
		const updatedGames = state.games.reduce<IGamesWithAdditionalProperty[]>((acc, restaurant) => {
			if (restaurant.restaurantId === restaurantID) {
				const filteredGames = restaurant.games.filter((el) => el.gameId !== gameID);
				acc.push({
					restaurantId: restaurant.restaurantId,
					restaurantAddress: restaurant.restaurantAddress,
					isChanged: true,
					games: filteredGames,
				});
				return acc;
			}
			acc.push(restaurant);
			return acc;
		}, []);
		dispatch({
			type: GameAssignmentActionTypes.SET_GAMES,
			payload: {
				games: updatedGames,
				IDsSelectedGame: state.IDsSelectedGame,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: GameAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: GameAssignmentActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const resetChanges = () => dispatch({ type: GameAssignmentActionTypes.RESET_CHANGES });

	const sendChanges = async () => {
		try {
			const dataForAssign = state.games.reduce<IAssignGames[]>((acc, restaurant) => {
				if (restaurant.isChanged) {
					acc.push({
						restaurantId: restaurant.restaurantId,
						gameIds: restaurant.games.map((el) => el.gameId),
					});
				}
				return acc;
			}, []);
			showLoader();
			await assignGames(dataForAssign);
			changeTypeOfModalWindow('success');
		} catch (err) {
			changeTypeOfModalWindow('error');
		} finally {
			hideLoader();
		}
	};

	const updateModuleDataAfterSaving = () => {
		refetch();
		setAllCustomers();
	};

	useEffect(() => {
		isMounted.current = true;
		refetch();
	}, [
		state.currentPage,
		state.countOfItems,
		state.searchString,
		state.selectedCategory,
		state.selectedDeveloper,
		state.isUnassignedGames,
		state.sortBy,
	]);

	useEffect(() => {
		if (state.activeItemToDisplay === 'games' && !state.games.length) {
			changeActiveItemToDisplay('restaurants');
		} else if (!state.restaurants.length) {
			changeActiveItemToDisplay('customers');
		}
	}, [state.restaurants, state.games]);

	useEffect(() => {
		setAllCustomers();
	}, []);

	return (
		<GameAssignmentContext.Provider
			value={{
				changeSearchString,
				changeSearchStringForAllCustomers,
				changeSearchStringForRestaurants,
				changeSearchStringForGames,
				setCategory,
				setDeveloper,
				toggleUnassignedGames,
				resetSelectedControls,
				setSortBy,
				setIDsSelectedGame,
				changeActiveItemToDisplay,
				setShowBreadcrumbs,
				setIDsSelectedCustomers,
				setIDsSelectedRestaurants,
				addSelectedGamesToRestaurant,
				deleteGameFromRestaurant,
				changeTypeOfModalWindow,
				changeCurrentPage,
				resetChanges,
				sendChanges,
				updateModuleDataAfterSaving,
				gamesForAssign: state.gamesForAssign,
				searchString: state.searchString,
				searchStringForAllCustomers: state.searchStringForAllCustomers,
				searchStringForRestaurants: state.searchStringForRestaurants,
				searchStringForGames: state.searchStringForGames,
				selectedCategory: state.selectedCategory,
				selectedDeveloper: state.selectedDeveloper,
				isUnassignedGames: state.isUnassignedGames,
				sortBy: state.sortBy,
				IDsSelectedGame: state.IDsSelectedGame,
				activeItemToDisplay: state.activeItemToDisplay,
				isShowBreadcrumbs: state.isShowBreadcrumbs,
				allCustomers: state.allCustomers,
				IDsSelectedCustomers: state.IDsSelectedCustomers,
				restaurants: state.restaurants,
				IDsSelectedRestaurants: state.IDsSelectedRestaurants,
				games: state.games,
				typeOfModalWindow: state.typeOfModalWindow,
				currentPage: state.currentPage,
				countOfItems: state.countOfItems,
			}}>
			{children}
		</GameAssignmentContext.Provider>
	);
};

export default GameAssignmentState;
